import { get as _get, toNumber as _toNumber } from 'lodash';
import moment from 'moment';
import { EntityId, LazadaStoreId } from '../../config/store-config';
import {
    FinanceFees,
    FinanceOrderRecord,
    emptyFees,
    financeRecordId,
    round2,
    sumFees,
} from './finance-record';

// Lazada → FinanceOrderRecord. Pure: no Angular, no injection, no clock.
//
// Lazada is the odd one of the three. Where Shopee and TikTok answer "tell me
// about this order", Lazada answers "tell me about this ledger" — one row per
// fee per order item, which this module pivots back into one record per order.
//
// The endpoint is /finance/transaction/details/get, PLURAL. The singular
// /finance/transaction/detail/get also resolves and returns
// InsufficientPermission ("App does not have permission to access this api")
// no matter what is granted in Lazada Open Platform. That error names the
// wrong cause and will send anyone who hits it to the permissions screen for
// nothing. Both store credentials were verified against the plural path.
//
// Lazada reports `VAT_in_amount` on every fee line, so claimable input tax on
// fees is read directly rather than derived — the only platform of the three
// that does this.

export interface LazadaNormalizeResult {
    records: FinanceOrderRecord[];
    // Fee names this map does not know. Lazada adds fee types over time, and a
    // silent fallthrough into `other` would hide a new charge indefinitely.
    // The caller is expected to surface these, not discard them.
    unmappedFeeNames: string[];
    // Lines Lazada returned with no order_no — account-level adjustments that
    // belong to the period but not to any order. Real money, so they are
    // reported rather than dropped; in the May–Jul sample these were
    // "Wrong Shipping Fee Adjustment" rows.
    unattributedTotal: number;
    unattributedLines: number;
    // Lines whose `amount` was present but did not parse as money, and so
    // contributed zero. Non-zero means revenue or a fee has silently gone
    // missing from a record — see parseAmount for the ฿1,000 case this
    // caught too late.
    unparsedAmounts: number;
}

// What /orders/get knows about an order that the finance ledger does not:
// when it was created (the tax point) and how its discount was funded.
export interface LazadaOrderFacts {
    // Epoch seconds, or null when created_at would not parse.
    createdAt: number | null;
    // Lazada-funded voucher on the order. Already INSIDE Item Price Credit —
    // Lazada pays the seller the full price and bears the voucher itself —
    // so this is attribution only and never changes the base. Verified on
    // lazada-genlogic 2026-10-23: order 1124398100860254 price 40,
    // voucher_platform 40, Item Price Credit 40.00; order 1116319088628254
    // price 560, voucher_platform 39.2, Item Price Credit 560.00.
    voucherPlatform: number;
    // Seller-funded voucher. Item Price Credit is taken to be net of it (the
    // seller bears it, so Lazada credits less); no 2026-09 order on either
    // store carried one, so that half is from Lazada's model, not measured.
    voucherSeller: number;
}

// Which bucket each fee_name lands in. Keys are `fee_name` exactly as Lazada
// spells them, verified against 500 rows across both stores, May–Jul 2026.
//
// Lazada signs these from the seller's point of view: fees arrive negative,
// credits positive. The buckets below want costs positive, so amounts are
// negated on the way in.
type FeeBucket = keyof Omit<FinanceFees, 'total'>;

const FEE_NAME_BUCKET: Record<string, FeeBucket> = {
    Commission: 'commission',
    'Payment Fee': 'transaction',
    'Shipping Fee Subsidy (By Seller)': 'shippingBorne',
    'Wrong Shipping Fee Adjustment': 'shippingBorne',
    'Sponsored Affiliates': 'affiliate',
    // Corrections and reversals of the above. Lazada raises these as separate
    // rows rather than amending the original, and they carry their own VAT, so
    // they belong in the same bucket as the charge they adjust. `Reversal
    // Commission` arrives POSITIVE — it is money coming back — and negating it
    // like any other fee row correctly reduces the commission total.
    'Commission fee - correction for undercharge': 'commission',
    'Payment fee - correction for undercharge': 'transaction',
    'Reversal Commission': 'commission',
    'Customer Return Delivery Fee': 'shippingBorne',
    'Shipping Fee Voucher Refund to Laz': 'shippingBorne',
    'Buyer Review Incentive': 'service',
};

// Rows that are NOT fees, and must never reach a fee bucket.
//
// This is the classification that was wrong. `Reversal Item Price` is a sale
// being undone — Lazada taking back revenue it previously credited — and
// `Lost Claim` is compensation paid TO the seller. Both were falling into
// fees.other, where the first inflated fees by the value of the refund and the
// second reduced them by the value of the income. Across Jan/Feb/May that was
// +851 and -1,284 respectively: individually large, nearly cancelling, and so
// invisible in a total that looked like -82.
//
// Keyed on transaction_type rather than fee_name because the type is the
// stable part — Lazada spells fee names freely (13 distinct in three months)
// but the `Refunds-Sales` / `Refunds-Claims` prefixes are structural.
const NON_FEE_TRANSACTION_TYPES: Record<string, 'refund' | 'compensation'> = {
    'Refunds-Sales': 'refund',
    'Refunds-Claims': 'compensation',
};

// Fallback when a fee_name is unrecognised. Lazada invents names freely, so a
// name-only map degrades into "everything new lands in other" — which is how
// eight distinct charges accumulated there unnoticed. The transaction_type
// prefix is coarse but structural, and puts a new charge in roughly the right
// place instead of a bucket nobody reads.
const TRANSACTION_TYPE_BUCKET: Record<string, FeeBucket> = {
    'Orders-Lazada Fees': 'commission',
    'Refunds-Lazada Fees': 'commission',
    'Other Services-Lazada Fees': 'service',
    'Other Services-Marketing Fees': 'affiliate',
    'Orders-Logistics': 'shippingBorne',
    'Refunds-Logistics': 'shippingBorne',
};

// The one fee_name that is not a fee: this is the sale itself.
const REVENUE_FEE_NAME = 'Item Price Credit';

// Lazada formats money as a display string, thousands separator included:
// an Item Price Credit of ฿1,560 arrives as "1,560.00". lodash's toNumber
// (and Number()) return NaN for that, and the old `num` turned NaN into 0 —
// so every Lazada line at or above ฿1,000 was booked as zero. Six 2026
// records across both stores had revenue 0 against real fees, three of them
// VAT rows for genlogic-co, and nothing flagged it: the Lazada verify probe
// counts orders, not money, and the settlement is built from the same parsed
// lines so it tied to itself. null, never 0, for a value that will not parse
// once separators are stripped, so the caller can count and report it.
export const parseAmount = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = _toNumber(String(value).replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : null;
};

const num = (source: unknown, path: string): number =>
    parseAmount(_get(source, path)) ?? 0;

// Lazada renders dates as "31 Jul 2026". Parsed strictly with the 'en'
// locale pinned: main.ts registers the Thai locale, and anything that switches
// moment's global locale to 'th' would otherwise make a lenient parse of an
// English month name silently yield the current date instead of failing.
const parseLazadaDate = (value: unknown): number | null => {
    if (typeof value !== 'string' || !value.trim()) {
        return null;
    }
    const parsed = moment(value.trim(), 'DD MMM YYYY', 'en', true);
    return parsed.isValid() ? parsed.unix() : null;
};

// The month a ledger line was booked in, as YYYY-MM — the record's part key.
// Taken from the line's own date string, so it cannot depend on the browser's
// timezone. A line whose date will not parse is kept in an 'undated' slice
// rather than dropped.
const lazadaLedgerMonth = (value: unknown): string => {
    const unix = parseLazadaDate(value);
    return unix === null ? 'undated' : moment.unix(unix).format('YYYY-MM');
};

export const normalizeLazadaOrders = (
    lines: unknown[],
    context: {
        storeId: LazadaStoreId;
        entityId: EntityId;
        // order_number -> facts joined from /orders/get. The ledger has no
        // order date of its own, and order creation is the tax point on
        // every platform — see the note in normalize-shopee.ts. Orders missing
        // from this map get a null tax point rather than a guess, and the sync
        // reports how many.
        orderFacts?: Record<string, LazadaOrderFacts>;
        fetchedAt: number;
        syncedAt: number;
    },
): LazadaNormalizeResult => {
    // One record per (order, ledger month). Lazada books an order's lines as
    // they happen — the sale and its commission on one day, an affiliate fee
    // or a claim days later — and those straddle month ends. Keyed per order,
    // the later month's sync replaced the whole order with only its own
    // lines: 1078732561864717's ฿85 January sale was overwritten by ฿5.46 of
    // February affiliate fee. Keyed by the month each line was booked in,
    // every month keeps its own slice whatever window a sync covers.
    const byPart = new Map<
        string,
        { orderNo: string; month: string; lines: unknown[] }
    >();
    const unmapped = new Set<string>();
    let unattributedTotal = 0;
    let unattributedLines = 0;
    let unparsedAmounts = 0;

    for (const line of lines) {
        const rawAmount = _get(line, 'amount');
        if (
            rawAmount !== null &&
            rawAmount !== undefined &&
            rawAmount !== '' &&
            parseAmount(rawAmount) === null
        ) {
            unparsedAmounts += 1;
        }
        const orderNo = String(_get(line, 'order_no') ?? '').trim();
        if (!orderNo) {
            unattributedTotal += num(line, 'amount');
            unattributedLines += 1;
            continue;
        }
        const month = lazadaLedgerMonth(_get(line, 'transaction_date'));
        const key = `${orderNo}|${month}`;
        const slice = byPart.get(key);
        if (slice) {
            slice.lines.push(line);
        } else {
            byPart.set(key, { orderNo, month, lines: [line] });
        }
    }

    const records: FinanceOrderRecord[] = [];

    byPart.forEach(({ orderNo, month, lines: orderLines }) => {
        const fees: FinanceFees = emptyFees();
        const adjustments = { refund: 0, compensation: 0, other: 0 };
        let revenue = 0;
        let feeVatTotal = 0;
        let latestTransaction: number | null = null;

        for (const line of orderLines) {
            const feeName = String(_get(line, 'fee_name') ?? '').trim();
            const amount = num(line, 'amount');

            const transactionDate = parseLazadaDate(
                _get(line, 'transaction_date'),
            );
            if (
                transactionDate !== null &&
                (latestTransaction === null ||
                    transactionDate > latestTransaction)
            ) {
                latestTransaction = transactionDate;
            }

            if (feeName === REVENUE_FEE_NAME) {
                revenue += amount;
                continue;
            }

            // Sales reversals and claims are movements of revenue, not fees.
            // Checked before any fee handling so they can never be negated
            // into a fee bucket — which is what made a refund look like a
            // charge and compensation look like a discount on charges.
            const transactionType = String(
                _get(line, 'transaction_type') ?? '',
            ).trim();
            const nonFee = NON_FEE_TRANSACTION_TYPES[transactionType];
            if (nonFee) {
                adjustments[nonFee] += amount;
                continue;
            }

            // VAT Lazada charged on its own fee — the seller's input tax.
            // VAT_in_amount is UNSIGNED: a Reversal Commission of +5.00
            // states its VAT as 0.33, the same way the −17.39 charge states
            // 1.14. Signed here from the amount, so a fee that is charged and
            // then reversed nets to zero input tax instead of doubling it
            // (41 reversal lines in 2026 carried ฿26.92 the wrong way).
            feeVatTotal +=
                -Math.sign(amount) * Math.abs(num(line, 'VAT_in_amount'));

            // Name first, then the transaction_type category. Only when both
            // miss is it genuinely unclassifiable, and only then is it
            // reported — so the warning means "Lazada invented something new",
            // not "Lazada used a word we happen not to list".
            const bucket =
                FEE_NAME_BUCKET[feeName] ??
                TRANSACTION_TYPE_BUCKET[transactionType];
            if (bucket) {
                // Negate: Lazada signs fees negative, buckets hold costs
                // positive. Reversal rows arrive positive and correctly
                // reduce their bucket.
                fees[bucket] += -amount;
            } else {
                unmapped.add(`${feeName} (${transactionType})`);
                fees.other += -amount;
            }
        }

        // Round each bucket before the total is taken. Lazada amounts are
        // satang-precise strings and summing three of them in binary float
        // (17.39 + 5.39 + 8.35) yields 31.130000000000003, which then refuses
        // to tie against a VAT base derived from the same figure.
        fees.commission = round2(fees.commission);
        fees.transaction = round2(fees.transaction);
        fees.service = round2(fees.service);
        fees.affiliate = round2(fees.affiliate);
        fees.shippingBorne = round2(fees.shippingBorne);
        fees.other = round2(fees.other);
        fees.total = sumFees(fees);
        revenue = round2(revenue);
        feeVatTotal = round2(feeVatTotal);

        const facts = context.orderFacts?.[orderNo];
        // The vouchers belong to the sale, so they are attributed to the
        // slice that carries the Item Price Credit — a later month holding
        // only an affiliate fee for the same order must not repeat them.
        const carriesSale = revenue !== 0;
        const voucherPlatform = carriesSale
            ? round2(facts?.voucherPlatform ?? 0)
            : 0;
        const voucherSeller = carriesSale
            ? round2(facts?.voucherSeller ?? 0)
            : 0;

        records.push({
            _id: financeRecordId(context.storeId, orderNo, month),
            storeId: context.storeId,
            platform: 'lazada',
            entityId: context.entityId,
            orderSn: orderNo,
            part: month,
            currency: 'THB',
            detailLevel: 'order-fees',

            // Joined from /orders/get — the ledger has neither date. Left null
            // when the order is not in the map rather than falling back to
            // transaction_date, which is when Lazada credited the account and
            // would put the sale in a later month than it is taxable in.
            orderDate: facts?.createdAt ?? null,
            taxPointDate: facts?.createdAt ?? null,
            settlementDate: latestTransaction,

            gross: {
                // Item Price Credit is what Lazada credits for the items:
                // the full price less the SELLER's voucher, with any
                // Lazada-funded voucher still inside it (Lazada bears that
                // itself — see LazadaOrderFacts). The ledger states no
                // split, so the two vouchers are joined from /orders/get
                // and attached here as attribution; the money is the same
                // with or without them.
                itemsBeforeDiscount: round2(revenue + voucherSeller),
                sellerDiscount: voucherSeller,
                platformSubsidy: voucherPlatform,
                // Lazada collects the buyer's shipping fee and ships the
                // parcel itself; it never reaches the seller's ledger, so
                // the seller supplies no delivery and the base carries none.
                buyerShippingPaid: 0,
                // What the buyer paid the seller for the items — the platform
                // voucher is the part Lazada paid instead.
                buyerTotal: round2(revenue - voucherPlatform),
                sellerRevenue: revenue,
                // Item Price Credit IS the consideration for the supply:
                // net of the seller's discount, inclusive of Lazada's. Not a
                // floor — the assessment's §2.1 worry that the platform
                // voucher was missing from it was checked against the order
                // API on 2026-10-23 and is not the case.
                taxableConsideration: revenue,
            },
            fees,
            adjustments: {
                refund: round2(adjustments.refund),
                compensation: round2(adjustments.compensation),
                other: round2(adjustments.other),
            },
            // Refunds and compensation move real money, so the settlement has
            // to carry them or it cannot tie to the bank. Both are already
            // signed as Lazada reports them — refunds negative, claims
            // positive — so they add rather than subtract.
            netSettlement: round2(
                revenue -
                    fees.total +
                    adjustments.refund +
                    adjustments.compensation,
            ),

            // Filled by the caller, which knows the entity's VAT status at the
            // resolved tax point.
            vat: null,
            // Lazada states this per line, so it is carried rather than
            // derived, and a slice with fees but no stated VAT (a
            // seller-funded shipping subsidy, say) records ZERO input tax
            // rather than null — null would invite the sync to estimate
            // 7/107 of a charge Lazada itself says carries no VAT. `base` is
            // back-solved from the stated VAT so the pair re-sums to the fee
            // exactly. The sync nulls this out for an entity that was not
            // registered at the tax point.
            feeVat:
                fees.total !== 0
                    ? {
                          base: round2(fees.total - feeVatTotal),
                          input: feeVatTotal,
                      }
                    : null,

            raw: {
                source: 'lazada:finance/transaction/details/get',
                fetchedAt: context.fetchedAt,
                payload: orderLines,
            },
            syncedAt: context.syncedAt,
        });
    });

    return {
        records,
        unmappedFeeNames: [...unmapped],
        unattributedTotal: round2(unattributedTotal),
        unattributedLines,
        unparsedAmounts,
    };
};
